type DeferredInitializerOptions<T extends object> = {
  keysToInitialize: (keyof T)[];
  onAllKeysInitialized: (values: T) => void;
  keysToAlwaysUpdate?: (keyof T)[]; // Keys that should trigger updates even after initialization
};

export interface DeferredInitializerReturnValue<T extends object> {
  setValue: (key: keyof T, value: T[keyof T]) => void;
  isInitialized: () => boolean;
}

/**
 * This helper can be used to defer initialization logic (usually in a component) until all required values have been set.
 *
 * Usage:
 *   - Save the return value (e.g., `this.deferredInitializer = createDeferredInitializerFor(...)`)
 *   - In each `@Input()` or `@ViewChild()` setter, call:
 *
 *       this.deferredInitializer.setValue('someKey', value);
 *
 *       -- OR, if more appropriate: --
 *
 *       if (value) this.deferredInitializer.setValue('someKey', value);
 *
 * Notes:
 *   - Subsequent calls to `setValue()` after initialization are ignored by default.
 *   - To override this, pass `keysToAlwaysUpdate` to allow specific keys to keep triggering updates, like for an `@Input()` we expect to change often. *
 *
 * Optional:
 *   - `isInitialized()` lets you check readiness from outside the initializer logic.
 *     This can be useful for:
 *       - Conditional rendering (e.g., `@if (deferredInitializer.isInitialized()) { ... }`)
 *       - Avoiding fallback logic (e.g., in `ngAfterViewInit`)
 *       - Debugging or unit testing (e.g., asserting readiness)
 */
export function createDeferredInitializer<T extends object>(options: DeferredInitializerOptions<T>): DeferredInitializerReturnValue<T> {
  const keysToInitialize: Set<keyof T> = new Set<keyof T>(options.keysToInitialize);
  const keysToAlwaysUpdate: Set<keyof T> = new Set<keyof T>(options.keysToAlwaysUpdate ?? []);
  const latestValuesPerKey: Partial<T> = {};
  const initializedKeys: Set<keyof T> = new Set<keyof T>();

  let initialized = false;

  function setValue<K extends keyof T>(key: K, value: T[K]): void {
    if (!keysToInitialize.has(key)) {
      throw new Error(`Key "${String(key)}" was passed to setValue(), but it wasn't included in keysToInitialize.`);
    }

    latestValuesPerKey[key] = value;
    initializedKeys.add(key);

    if (initialized) {
      if (keysToAlwaysUpdate.has(key)) {
        options.onAllKeysInitialized(latestValuesPerKey as T);
      } // else ignore changes after initialization for this key
    } else if (initializedKeys.size === keysToInitialize.size) {
      initialized = true;
      options.onAllKeysInitialized(latestValuesPerKey as T);
    }
  }

  function isInitialized(): boolean {
    return initialized;
  }

  return {setValue, isInitialized};
}
